/**
 * The business: equipment, productivity, and the execution of yard work.
 *
 * The central relationship of the whole game lives in `workMinutesFor` and
 * `qualityFor`: buying a better tool visibly cuts the minutes a job takes and
 * raises the quality that drives reputation and repeat work. Everything else -
 * more leads, better pay, employees - hangs off those two numbers.
 *
 * Implemented against a `ServiceLineDef` rather than hard-coded to lawns, so a
 * second vertical post-V1 is a data change (ADR-0003).
 */
import type { GameState, ActiveWork, Lead, Customer, WorkTask } from '../state/GameState.js';
import type { EventBus } from '../core/events.js';
import type { Rng } from '../core/rng.js';
import type { FinanceSystem } from './FinanceSystem.js';
import type { ReputationSystem } from './ReputationSystem.js';
import { BALANCE } from '../data/balance.js';
import {
  traitsOf, difficultyPenalty, expectationBar, relationshipOf, TRAIT_RANGE,
} from './CustomerSystem.js';

/** Centre for the expectation effects here, matching CustomerSystem's. */
const MEAN_EXPECTATION =
  (TRAIT_RANGE.expectation[0] + TRAIT_RANGE.expectation[1]) / 2;
import {
  EQUIPMENT,
  EMPLOYEE_KIT,
  SERVICE_LINES,
  getEquipment,
  type EquipmentDef,
  type EquipmentSlot,
} from '../data/catalog.js';

export class BusinessSystem {
  constructor(
    private readonly bus: EventBus,
    private readonly rng: Rng,
  ) {}

  private serviceLine() {
    return SERVICE_LINES[0]!;
  }

  /**
   * Condition of an owned tool, 0..1. A tool with no record is new.
   *
   * The default matters beyond migration: anything added to the catalogue
   * later, and the employee kit, start in working order rather than broken.
   */
  static conditionOf(state: GameState, id: string): number {
    const value = state.business.equipmentCondition[id];
    return value === undefined ? 1 : Math.max(0, Math.min(1, value));
  }

  /**
   * A worn tool's effective speed.
   *
   * Falls toward a floor rather than toward zero: a neglected mower is a bad
   * version of itself, never a brick. Note that a brand-new push mower has
   * `speed` 1.0, so wear costs a beginner with nothing else nothing at all -
   * the first hour of the game must not be about maintenance.
   */
  static effectiveSpeed(def: EquipmentDef, condition: number): number {
    const scale = BALANCE.equipment.wornSpeedFloor
      + (1 - BALANCE.equipment.wornSpeedFloor) * condition;
    return 1 + (def.speed - 1) * scale;
  }

  /**
   * The quality a slot's cheapest tool gives. Wear never takes you below it.
   *
   * Computed from the catalogue rather than written down, so adding a tool
   * cannot silently move the floor out from under this rule.
   */
  static baselineQuality(slot: EquipmentSlot): number {
    let lowest = Infinity;
    for (const def of EQUIPMENT) {
      if (def.slot === slot) lowest = Math.min(lowest, def.quality);
    }
    return Number.isFinite(lowest) ? lowest : 0;
  }

  /**
   * A worn tool's effective quality contribution.
   *
   * Wear takes away the upgrade you paid for, never the ability to do the
   * job: a neglected zero-turn finishes like a push mower, and a neglected
   * push mower finishes like a push mower.
   *
   * The first version scaled `quality` straight down from zero, and the
   * balance sweep said what that meant in one line: `never-invests` - a
   * player with a $60 mower and nothing else - fell from $58,297 to $20,261.
   * Wear was charging the poorest player hardest for owning the cheapest
   * possible tool, which is the exact opposite of the intent written into
   * BALANCE.equipment. Degrading toward the slot's baseline instead makes
   * maintenance a mid-game decision about protecting an investment, and
   * leaves the first hour of the game alone. It also mirrors what speed
   * already did, where a 1.0 tool has no bonus to lose.
   */
  static effectiveQuality(def: EquipmentDef, condition: number): number {
    const baseline = Math.min(def.quality, BusinessSystem.baselineQuality(def.slot));
    const scale = BALANCE.equipment.wornQualityFloor
      + (1 - BALANCE.equipment.wornQualityFloor) * condition;
    return baseline + (def.quality - baseline) * scale;
  }

  /**
   * The best owned item in each slot; the player always works with their best.
   *
   * "Best" is by nameplate speed, not by current condition. That is
   * deliberate: a player who has bought a commercial mower should be working
   * with it, and silently reverting them to the push mower they kept in the
   * shed because the good one needs a service would be a decision made for
   * them, invisibly, in the one place they are paying most attention.
   */
  bestInSlot(state: GameState, slot: EquipmentSlot): EquipmentDef | null {
    let best: EquipmentDef | null = null;
    for (const id of state.business.equipment) {
      const def = getEquipment(id);
      if (def.slot !== slot) continue;
      if (!best || def.speed > best.speed) best = def;
    }
    return best;
  }

  /** What a full service of one tool costs right now. */
  serviceCost(state: GameState, id: string): number {
    const def = getEquipment(id);
    const wear = 1 - BusinessSystem.conditionOf(state, id);
    return Math.round(def.price * BALANCE.equipment.serviceCostFraction * wear);
  }

  /**
   * Service a tool back to new.
   *
   * Returns false when there is nothing to do or no money, and never
   * part-services: a half-paid repair is a worse deal than the player thinks
   * they are getting.
   */
  serviceEquipment(state: GameState, id: string, finance: FinanceSystem): boolean {
    if (!state.business.equipment.includes(id)) return false;
    const cost = this.serviceCost(state, id);
    if (cost <= 0) return false;
    const def = getEquipment(id);
    if (!finance.debit(state, cost, 'equipment', `Service - ${def.name}`)) return false;
    state.business.equipmentCondition[id] = 1;
    this.bus.emit({
      type: 'equipment.serviced', itemId: id, name: def.name, cost,
    });
    return true;
  }

  /**
   * Wear the kit that was actually used, by how long the work took.
   *
   * Only the tools the job used, and only the best in each slot, since that
   * is what `speedMultiplier` and `qualityBase` read. A trimmer sitting in
   * the shed does not go blunt.
   */
  wearKit(state: GameState, minutes: number): void {
    const line = this.serviceLine();
    const loss = (minutes / 60) * BALANCE.equipment.wearPerHour;
    if (loss <= 0) return;
    for (const slot of line.slots) {
      const item = this.bestInSlot(state, slot);
      if (!item) continue;
      const now = BusinessSystem.conditionOf(state, item.id);
      const next = Math.max(0, now - loss);
      state.business.equipmentCondition[item.id] = next;
      // Announce the crossing, once. A warning that repeats every tick is
      // noise the player learns to ignore, which is worse than no warning:
      // the one that matters arrives looking like the forty before it.
      const bar = BALANCE.equipment.warnBelow;
      if (now >= bar && next < bar) {
        this.bus.emit({
          type: 'equipment.worn', itemId: item.id, name: item.name, condition: next,
        });
      }
    }
  }

  canWork(state: GameState): boolean {
    return this.bestInSlot(state, this.serviceLine().requiredSlot) !== null;
  }

  /**
   * Combined speed multiplier from the owned kit. The required slot sets the
   * baseline; supporting tools each add a fraction of their bonus, so a full
   * kit is meaningfully better than a great mower alone but does not run away.
   */
  speedMultiplier(state: GameState): number {
    const line = this.serviceLine();
    const primary = this.bestInSlot(state, line.requiredSlot);
    if (!primary) return 0;
    let mult = BusinessSystem.effectiveSpeed(
      primary, BusinessSystem.conditionOf(state, primary.id),
    );
    for (const slot of line.slots) {
      if (slot === line.requiredSlot) continue;
      const item = this.bestInSlot(state, slot);
      if (!item) continue;
      const speed = BusinessSystem.effectiveSpeed(
        item, BusinessSystem.conditionOf(state, item.id),
      );
      mult += (speed - 1) * 0.9;
    }
    return mult;
  }

  /** Quality is the sum of the kit's contributions, capped at 1. */
  qualityBase(state: GameState): number {
    const line = this.serviceLine();
    let q = 0;
    for (const slot of line.slots) {
      const item = this.bestInSlot(state, slot);
      if (!item) continue;
      q += BusinessSystem.effectiveQuality(
        item, BusinessSystem.conditionOf(state, item.id),
      );
    }
    return Math.min(1, q);
  }

  /** In-game minutes this lead takes, for the player with their current kit. */
  workMinutesFor(state: GameState, lead: Lead, performance: number): number {
    // Difficulty is separate from size on purpose: an overgrown small garden
    // with three trees in it is more work than a flat large lawn, and the pay
    // on the board already reflects that. Without it, difficulty would be a
    // pay bonus with no cost attached.
    const base = BALANCE.yard.minutesBySize[lead.lotSize];
    // Floor low enough that a critical need is genuinely crippling. At 0.4 the
    // floor was swallowing the critical-needs penalty entirely, which is why an
    // early sweep found that never eating was the winning strategy.
    const speed = this.speedMultiplier(state) * Math.max(0.2, performance);
    if (speed <= 0) return Infinity;
    return Math.max(6, Math.round(base / speed));
  }

  /** What the shop will sell right now, given owned items and reputation. */
  purchasable(state: GameState): EquipmentDef[] {
    return EQUIPMENT.filter((def) => {
      if (state.business.equipment.includes(def.id)) return false;
      if (def.requiresReputation && state.player.reputation < def.requiresReputation) return false;
      return true;
    });
  }

  buyEquipment(state: GameState, id: string, finance: FinanceSystem): boolean {
    const def = EQUIPMENT.find((e) => e.id === id);
    if (!def) return false;
    if (state.business.equipment.includes(id)) return false;
    if (def.requiresReputation && state.player.reputation < def.requiresReputation) return false;
    if (!finance.debit(state, def.price, 'equipment', def.name)) return false;
    state.business.equipment.push(id);
    state.business.lifetimeEquipmentSpend += def.price;
    this.bus.emit({
      type: 'equipment.purchased',
      itemId: def.id,
      name: def.name,
      price: def.price,
    });
    return true;
  }

  /** A crew kit is what makes an extra pair of hands actually useful. */
  buyCrewKit(state: GameState, finance: FinanceSystem): boolean {
    if (!finance.debit(state, EMPLOYEE_KIT.price, 'equipment', EMPLOYEE_KIT.name)) return false;
    state.business.crewKits += 1;
    state.business.lifetimeEquipmentSpend += EMPLOYEE_KIT.price;
    this.bus.emit({
      type: 'equipment.purchased',
      itemId: EMPLOYEE_KIT.id,
      name: EMPLOYEE_KIT.name,
      price: EMPLOYEE_KIT.price,
    });
    return true;
  }

  canRegister(state: GameState): boolean {
    return (
      !state.business.registered &&
      state.player.reputation >= BALANCE.business.registrationMinReputation &&
      state.finance.cash >= BALANCE.business.registrationFee
    );
  }

  registerBusiness(state: GameState, name: string, finance: FinanceSystem): boolean {
    if (!this.canRegister(state)) return false;
    if (!finance.debit(state, BALANCE.business.registrationFee, 'fees', 'Business registration'))
      return false;
    state.business.registered = true;
    state.business.name = name.trim() || 'Grove Yard Care';
    this.bus.emit({
      type: 'notice',
      level: 'good',
      message: `${state.business.name} is registered. You can now sign contracts and hire.`,
    });
    return true;
  }

  /**
   * What this property needs doing, given what the player owns.
   *
   * The rule that keeps this from breaking the economy: a task appears only
   * when the player has the tool for it. Not owning a trimmer does not mean
   * failing the edging - it means the edging is not part of this job. The
   * cost of an incomplete kit is already priced into `qualityBase`, and
   * charging for it again here would be a second, hidden penalty on the
   * player least able to absorb one.
   *
   * So buying a trimmer adds something to do rather than something to lose,
   * which is the whole point of the change: the equipment slots were a
   * number that went up, and they are now an afternoon that looks different.
   */
  tasksFor(state: GameState, lead: Lead, customer: Customer | null): WorkTask[] {
    const raw: { kind: WorkTask['kind']; weight: number }[] = [
      { kind: 'mow', weight: 1 },
    ];
    // Edging matters on anything bigger than a patch of grass, and on a
    // property that has been let go - which is what the difficulty trait
    // says in words on the job card.
    const overgrown = customer ? traitsOf(customer).difficulty > 0.62 : false;
    if (this.bestInSlot(state, 'trimmer') && (lead.lotSize !== 'small' || overgrown)) {
      raw.push({ kind: 'trim', weight: 0.42 });
    }
    // Clearing up after yourself: only worth a pass where there is enough of
    // a yard to leave a mess in.
    const messy = lead.lotSize === 'large' || overgrown;
    const canClear = this.bestInSlot(state, 'blower') ?? this.bestInSlot(state, 'hauling');
    if (canClear && messy) raw.push({ kind: 'clear', weight: 0.3 });

    const total = raw.reduce((sum, t) => sum + t.weight, 0);
    return raw.map((t) => ({ kind: t.kind, weight: t.weight / total, progress: 0 }));
  }

  /**
   * How much of the job was actually done, as one number.
   *
   * The weighted sum of the tasks. With only a mower this is exactly the old
   * single coverage figure, which is what keeps every existing balance
   * assumption intact.
   */
  static coverageOf(tasks: WorkTask[]): number {
    if (tasks.length === 0) return 1;
    let done = 0;
    for (const task of tasks) done += task.weight * Math.max(0, Math.min(1, task.progress));
    return Math.max(0, Math.min(1, done));
  }

  /** Begin working a lead the player has accepted and travelled to. */
  startWork(
    state: GameState,
    lead: Lead,
    performance: number,
    customer: Customer | null = null,
  ): ActiveWork | null {
    if (!this.canWork(state)) return null;
    if (state.player.activeWork) return null;
    const work: ActiveWork = {
      kind: 'yard',
      refId: lead.id,
      locationId: lead.locationId,
      title: `${lead.lotSize} yard - ${lead.customerName}`,
      totalMinutes: this.workMinutesFor(state, lead, performance),
      elapsedMinutes: 0,
      basePay: lead.payout,
      quality: this.qualityBase(state),
      coverage: 1,
      tasks: this.tasksFor(state, lead, customer),
    };
    state.player.activeWork = work;
    this.bus.emit({ type: 'job.started', jobId: lead.id, title: work.title });
    return work;
  }

  /**
   * Advance the current job. Returns the completed work when it finishes, so
   * the caller can settle payment. Progress is driven by in-game minutes, which
   * is what makes doing the work yourself feel like it costs your day.
   */
  progressWork(state: GameState, minutes: number): ActiveWork | null {
    const work = state.player.activeWork;
    if (!work) return null;
    work.elapsedMinutes += minutes;
    // Wear as the work happens rather than on completion, so a job abandoned
    // half way through still costs the kit what it used.
    if (work.kind === 'yard') this.wearKit(state, minutes);
    if (work.elapsedMinutes < work.totalMinutes) return null;
    work.quality = BusinessSystem.qualityAfterCoverage(work.quality, work.coverage);
    state.player.activeWork = null;
    return work;
  }

  /**
   * Fold how much of the lawn was actually cut into the job's quality.
   *
   * The floor is deliberately well below the "good work" threshold: a player
   * who stands still while the clock runs finishes the job, gets paid, and
   * gets a visibly worse result for it - no tip, less reputation, and a
   * customer less likely to come back. Doing the work is worth doing.
   */
  static qualityAfterCoverage(quality: number, coverage: number): number {
    const clamped = Math.max(0, Math.min(1, coverage));
    return quality * (0.6 + 0.4 * clamped);
  }

  cancelWork(state: GameState): void {
    const work = state.player.activeWork;
    if (!work) return;
    state.player.activeWork = null;
    this.bus.emit({
      type: 'job.failed',
      jobId: work.refId,
      title: work.title,
      reason: 'abandoned',
    });
  }

  /**
   * Map a kit's raw quality contribution onto the 0..1 result scale.
   *
   * Calibrated so the progression is felt at every step and never punishes the
   * player for being at the start:
   *   - starter mower alone (base 0.42) -> ~0.755: reliably "good", never a tip
   *   - full basic kit      (base 0.71) -> ~0.863: good, close to excellent
   *   - self-propelled kit  (base 0.81) -> ~0.900: reliably excellent, tips
   *   - top kit             (base 1.00) -> ~0.970
   *
   * The thresholds it is measured against are qualityGoodThreshold (0.70) and
   * tipQualityThreshold (0.88). An exhausted worker loses about 5%, which is
   * enough to push starter-kit work below "good" - working while wrecked is a
   * real risk, but only at the bottom of the ladder.
   */
  static readonly QUALITY_INTERCEPT = 0.5993;
  static readonly QUALITY_SLOPE = 0.3707;

  qualityCurve(base: number, performance: number, variance: number): number {
    const raw = BusinessSystem.QUALITY_INTERCEPT + BusinessSystem.QUALITY_SLOPE * base;
    return clamp01(raw * (0.82 + 0.18 * performance) + variance);
  }

  /** Final quality for a job the player did themselves. */
  finalQuality(base: number, performance: number): number {
    return this.qualityCurve(base, performance, this.rng.float(-0.04, 0.04));
  }

  /** Settle a finished yard job: pay, tip, reputation. */
  settleWork(
    state: GameState,
    work: ActiveWork,
    performance: number,
    finance: FinanceSystem,
    reputation: ReputationSystem,
    /**
     * The customer, when there is one. Their expectation sets the bar the
     * work has to clear to earn anything, and their generosity sets how much
     * that is - so the same finish is a tip from one household and a shrug
     * from the one next door.
     */
    customer: Customer | null = null,
  ): { pay: number; quality: number; tip: number } {
    // A difficult property drags the finish down, and better equipment is
    // what pulls it back up - which is what ties the client you choose to the
    // kit you have bought.
    const quality = Math.max(0, Math.min(1,
      this.finalQuality(work.quality, performance)
      - (customer ? difficultyPenalty(customer) : 0),
    ));
    const pay = Math.round(work.basePay * (0.85 + 0.15 * quality));
    finance.credit(state, pay, 'job', work.title);
    state.business.lifetimeRevenue += pay;

    let tip = 0;
    // Anchored on the global threshold and moved by expectation, exactly as
    // the satisfaction bar is: an average customer behaves as they always
    // did, so the balance sweep's assumptions still hold.
    const traits = customer ? traitsOf(customer) : null;
    const tipBar = BALANCE.yard.tipQualityThreshold
      + (traits ? (traits.expectation - MEAN_EXPECTATION) * 0.08 : 0);
    if (quality >= tipBar) {
      // Generosity scales it, and clearing the bar by a margin scales it
      // further - a job that is merely acceptable to a fussy customer is not
      // the same as one that impresses them.
      const margin = Math.min(1, (quality - tipBar) / 0.12);
      // A household that has had you back six times tips like it. This is
      // the one place loyalty is allowed to add money rather than move it,
      // and it is small and earned: six good jobs for the same address.
      const rung = customer ? relationshipOf(customer) : 'new';
      const loyaltyBonus = rung === 'loyal'
        ? BALANCE.leads.loyaltyTipBonus
        : rung === 'regular' ? BALANCE.leads.loyaltyTipBonus * 0.5 : 0;
      const rate = BALANCE.yard.tipRate
        * (traits ? 0.55 + traits.generosity * 0.9 : 1)
        * (0.75 + margin * 0.5)
        * (1 + loyaltyBonus);
      tip = Math.round(pay * rate);
      finance.credit(state, tip, 'tip', `Tip - ${work.title}`);
      state.business.lifetimeRevenue += tip;
    }

    reputation.applyJobResult(
      state, quality,
      customer ? expectationBar(customer) : undefined,
    );
    state.statistics.jobsCompleted += 1;

    this.bus.emit({
      type: 'job.completed',
      jobId: work.refId,
      title: work.title,
      pay: pay + tip,
      quality,
    });
    return { pay, quality, tip };
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
